import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { PROMO_WIDGET_OVERLAP, PromoData, PromoWidget, PromoWidgetItem } from '@/components/promo-widget/PromoWidget';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useOddsFormat } from '@/hooks/use-odds-format';
import { PromotionType } from '@/types/api.generated';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { sbkBetslipPromoKeys, useSbkBetslipPromos } from '../hooks/use-sbk-betslip-promos';
import { useSbkBetSlipStore } from '../hooks/use-sbk-betslip-store';
import { selectedPromoSelector } from '../utils/betslip-selectors';
import { getPromoCriteriaErrorMessages, isPromoVisibleForBet } from '../utils/promo-criteria/criterion-validators';

type SbkPromoWidgetProps = PropsWithChildren<{
    betId: string;
}>;

/**
 * Wraps a card with the promo widget above it, applying the overlap style.
 * If no promos are available, renders children without the widget.
 */
export const SbkPromoWidget = ({ betId, children }: SbkPromoWidgetProps) => {
    const { t } = useTranslation('betslip_sbk');
    const { t: tPromoErrors } = useTranslation('sbk_promo_errors');
    const { promos } = useSbkBetslipPromos();
    const selectedPromo = useSbkBetSlipStore(selectedPromoSelector(betId));
    // sorted so shallow comparison is stable across entry add/remove order
    const idsUsedByOtherBets = useSbkBetSlipStore(
        useShallow(state =>
            Object.entries(state.selectedPromos)
                .flatMap(([id, promo]) => (id !== betId && promo ? [promo.campaign_token] : []))
                .sort()
        )
    );
    const setSelectedPromo = useSbkBetSlipStore(state => state.actions.setSelectedPromo);
    const removeSelectedPromo = useSbkBetSlipStore(state => state.actions.removeSelectedPromo);
    const isBonusEnabled = useSbkBetSlipStore(state => !!state.isBetrBucksEnabled[betId]);
    const oddsFormat = useOddsFormat();
    const queryClient = useQueryClient();
    const { formatCurrency } = useFormatCurrency();
    const visiblePromos = useMemo(() => promos.filter(promo => isPromoVisibleForBet(promo, betId)), [promos, betId]);
    const criterionErrorMessages = useSbkBetSlipStore(
        useShallow(state => getPromoCriteriaErrorMessages(visiblePromos, state, betId, tPromoErrors, oddsFormat))
    );

    const promosWithStatus: PromoWidgetItem[] = useMemo(() => {
        const usedIds = new Set(idsUsedByOtherBets);
        return visiblePromos.map(promo => {
            const description =
                promo.type === PromotionType.PlayAndGet
                    ? t('play_and_get_progress', {
                          current: formatCurrency(promo.progress_amount ?? 0),
                          target: formatCurrency(promo.metadata.TARGET_AMOUNT ?? 0),
                      })
                    : promo.description;
            const mappedPromo: PromoData = { id: promo.campaign_token, name: promo.title, description };
            if (isBonusEnabled) {
                return {
                    ...mappedPromo,
                    status: 'available',
                    error: { shortDescription: t('non_applicable_with_bonus') },
                };
            }
            const criterionErrorMessage = criterionErrorMessages[promo.campaign_token];
            if (criterionErrorMessage) {
                return {
                    ...mappedPromo,
                    status: 'available',
                    error: { shortDescription: criterionErrorMessage },
                };
            }
            if (promo.campaign_token === selectedPromo?.campaign_token) {
                return { ...mappedPromo, status: 'applied' };
            }
            const isInUse = usedIds.has(promo.campaign_token);
            const canBeMultiEntry = promo.type === PromotionType.PlayAndGet;
            if (isInUse && !canBeMultiEntry) {
                return { ...mappedPromo, status: 'in-use' };
            }
            return { ...mappedPromo, status: 'available' };
        });
    }, [visiblePromos, criterionErrorMessages, idsUsedByOtherBets, selectedPromo, isBonusEnabled, t, formatCurrency]);

    const handleSelect = useCallback(
        (promoId: string) => {
            if (selectedPromo?.campaign_token === promoId) {
                removeSelectedPromo(betId);
                return;
            }
            setSelectedPromo(betId, visiblePromos.find(p => p.campaign_token === promoId) ?? null);
        },
        [betId, selectedPromo, setSelectedPromo, removeSelectedPromo, visiblePromos]
    );

    const handleExpand = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: sbkBetslipPromoKeys.all });
    }, [queryClient]);

    if (visiblePromos.length === 0) {
        return <>{children}</>;
    }

    return (
        <>
            <PromoWidget promos={promosWithStatus} onSelectPromo={handleSelect} onExpand={handleExpand} />
            <View style={styles.cardWithPromo}>{children}</View>
        </>
    );
};

const styles = StyleSheet.create({
    cardWithPromo: {
        marginTop: PROMO_WIDGET_OVERLAP,
        position: 'relative',
    },
});
